import { useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Z } from '../../lib/zIndex'
import { useDialogBackHandler } from '../../hooks/useDialogBackHandler'
import type { TFunction } from 'i18next'
import type { Notification } from '../../types/notifications'
import { timeAgo, localizedText } from './format'

interface NotificationPanelProps {
  isOpen: boolean
  onClose: () => void
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  hasMore: boolean
  /** Shown in the footer when the caller can open the full inbox page. */
  seeAllTo?: string
  markRead: (id: string) => void
  markAllRead: () => void
  archiveAll: () => void
  loadMore: () => void
}

function getModuleLabel(sourceModule: string, t: TFunction): string {
  const key = sourceModule.replace(/-module$/, '')
  return t(`notifications.modules.${key}`, key)
}

function moduleBadgeColor(module: string): string {
  if (module.includes('procurement')) return 'bg-primary-100 text-primary-700'
  return 'bg-slate-100 text-slate-600'
}

export default function NotificationPanel({
  isOpen,
  onClose,
  notifications,
  unreadCount,
  loading,
  hasMore,
  seeAllTo,
  markRead,
  markAllRead,
  archiveAll,
  loadMore,
}: NotificationPanelProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const panelRef = useRef<HTMLDivElement>(null)
  // Back / swipe-back → close (the browser gesture; not a keyboard/pointer event).
  useDialogBackHandler(isOpen, onClose)

  // Close when clicking outside (desktop dropdown).
  useEffect(() => {
    if (!isOpen) return
    function handler(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, onClose])

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  if (!isOpen) return null

  function handleItemClick(notif: Notification) {
    markRead(notif.id)
    if (notif.entity_url) {
      navigate(notif.entity_url)
    }
    onClose()
  }

  return (
    <div
      ref={panelRef}
      className={`fixed inset-0 z-[${Z.modal}] flex flex-col overflow-hidden bg-white sm:absolute sm:inset-auto sm:end-0 sm:top-full sm:mt-2 sm:w-96 sm:rounded-xl sm:shadow-xl sm:border sm:border-slate-200 sm:max-h-[calc(100vh-5rem)]`}
    >
      {/* Safe area spacer for mobile status bar (only applies to full-screen overlay) */}
      <div className="pt-safe sm:hidden" />
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="text-sm font-semibold text-slate-800">
          {t('notifications.title', 'Notifications')}
          {unreadCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center rounded-full bg-primary-600 text-white text-2xs font-semibold px-1.5 min-w-[1.1rem] h-[1.1rem]">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </span>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="text-xs text-primary-600 hover:text-primary-800 font-medium"
            >
              {t('notifications.markAllRead', 'Mark all read')}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-600 p-0.5 rounded-sm"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* List */}
      <div className="overflow-y-auto overscroll-contain flex-1">
        {loading && notifications.length === 0 && (
          <div className="space-y-2 p-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 rounded-lg bg-slate-100 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && notifications.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-10">
            {t('notifications.empty', 'No notifications yet')}
          </p>
        )}

        {notifications.map((notif) => (
          <button
            key={notif.id}
            type="button"
            onClick={() => handleItemClick(notif)}
            className={`w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-b-0 ${!notif.read ? 'bg-primary-50/40' : ''}`}
          >
            {/* Unread dot */}
            <span className={`mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${notif.read ? 'bg-transparent' : 'bg-primary-500'}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-2xs font-semibold px-1.5 py-0.5 rounded-full ${moduleBadgeColor(notif.source_module)}`}>
                  {getModuleLabel(notif.source_module, t)}
                </span>
                <span className="text-2xs text-slate-500">{timeAgo(notif.created_at, t)}</span>
              </div>
              <p className="text-xs font-semibold text-slate-800 mt-0.5 truncate">{localizedText(notif.title_i18n, notif.title, i18n.language)}</p>
              {notif.body && (
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{localizedText(notif.body_i18n, notif.body, i18n.language)}</p>
              )}
            </div>
          </button>
        ))}

        {hasMore && (
          <button
            type="button"
            disabled={loading}
            onClick={loadMore}
            className="w-full text-xs text-primary-600 hover:text-primary-800 py-3 font-medium disabled:opacity-50"
          >
            {loading ? t('common.loading', 'Loading…') : t('notifications.loadMore', 'Load more')}
          </button>
        )}
      </div>

      {/* Footer */}
      {(notifications.length > 0 || seeAllTo) && (
        <div className="border-t border-slate-100 px-4 py-2 flex items-center justify-between">
          {seeAllTo ? (
            <Link
              to={seeAllTo}
              onClick={onClose}
              className="text-xs font-medium text-primary-600 hover:text-primary-800"
            >
              {t('notifications.seeAll', 'See all')}
            </Link>
          ) : (
            <span />
          )}
          {notifications.length > 0 && (
            <button
              type="button"
              onClick={archiveAll}
              className="text-xs text-slate-500 hover:text-slate-600"
            >
              {t('notifications.archiveAll', 'Archive all')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
